// src/_utils/chatFlowManager.js - Coordinator for AI and preset flow
export class ChatFlowManager {
  constructor() {
    this.stages = {
      INITIAL: "INITIAL",
      ACTIVITY: "ACTIVITY",
      CUISINE: "CUISINE",
      TIER: "TIER",
      COMPLETE: "COMPLETE",
      CUSTOM: "CUSTOM",
    };

    this.stageFlow = [
      this.stages.INITIAL,
      this.stages.ACTIVITY,
      this.stages.CUISINE,
      this.stages.TIER,
      this.stages.COMPLETE,
    ];

    this.stageOptions = {
      [this.stages.ACTIVITY]: {
        options: ["Adventure", "Nature", "City Strolls", "Cultural Immersion"],
        aiPrompt: "Help user choose activity type",
        successMessage:
          "Great choice! {choice} is a fantastic way to explore Montreal.",
      },
      [this.stages.CUISINE]: {
        options: ["French", "Italian", "Local Specialties", "Asian Fusion"],
        aiPrompt: "Ask about food preferences",
        successMessage:
          "Excellent! {choice} cuisine will make your journey even more memorable.",
      },
      [this.stages.TIER]: {
        options: ["Basic", "Signature", "Luxury"],
        aiPrompt: "Understand budget preference",
        successMessage:
          "Perfect! I'll craft a {choice} tier itinerary just for you.",
      },
    };
  }

  // Get current stage based on user choices
  getCurrentStage(userChoices) {
    if (!userChoices || Object.keys(userChoices).length === 0) {
      return this.stages.ACTIVITY;
    }

    if (userChoices.activity && !userChoices.cuisine) {
      return this.stages.CUISINE;
    }

    if (userChoices.cuisine && !userChoices.tier) {
      return this.stages.TIER;
    }

    if (userChoices.tier) {
      return this.stages.COMPLETE;
    }

    return this.stages.CUSTOM;
  }

  // Process user input and decide response type
  async processUserInput(message, context) {
    const { userChoices, hasItinerary, aiService } = context;
    const currentStage = this.getCurrentStage(userChoices);
    const trimmedMessage = message.trim();
    if (context.candidates && context.candidates.length > 0) {
      const matched = context.candidates.find(
        (c) => c.name.toLowerCase() === trimmedMessage.toLowerCase()
      );
      if (matched) {
        console.log("match",matched)
        return {
          type: "user_input_choice",
          stage: currentStage,
          choice: matched, // ✅ 改这里
          response: `Got it! You want to go to ${matched.name}.`,
        };
      }
    }
    const { intent, entity } = await aiService.extractIntentEntity(message);
  if (intent && intent.startsWith("change_")) {
    return {
      type: "modify_choices",
      intent,
      entity,
    };
  }

    // 1. Check if it's a direct choice matching preset options
    const directChoice = this.checkDirectChoice(message, currentStage);
    if (directChoice) {
      return {
        type: "direct_choice",
        stage: currentStage,
        choice: directChoice,
        response: this.getSuccessMessage(currentStage, directChoice),
      };
    }

    // 2. Check for special commands
    const command = this.checkCommands(message);
    if (command) {
      return command;
    }

    // 3. If itinerary exists, use pure AI mode
    if (hasItinerary) {
      return {
        type: "ai_chat",
        useAI: true,
      };
    }

    // 4. Hybrid mode: AI response + guide to next step
    if (
      currentStage !== this.stages.COMPLETE &&
      currentStage !== this.stages.CUSTOM
    ) {
      return {
        type: "guided_chat",
        stage: currentStage,
        useAI: true,
        showOptionsAfter: true,
      };
    }

    // 5. Default AI chat
    return {
      type: "ai_chat",
      useAI: true,
    };
  }

  // Check if message directly matches an option
  checkDirectChoice(message, stage) {
    if (!this.stageOptions[stage]) return null;

    const options = this.stageOptions[stage].options;
    const normalizedMessage = message.toLowerCase().trim();

    for (const option of options) {
      if (
        normalizedMessage === option.toLowerCase() ||
        normalizedMessage.includes(option.toLowerCase())
      ) {
        return option;
      }
    }

    return null;
  }

  // Check for special commands
  checkCommands(message) {
    const lower = message.toLowerCase();

    if (lower.includes("quick generate") || lower.includes("skip")) {
      return {
        type: "command",
        command: "quick_generate",
        defaultChoices: {
          activity: "City Strolls",
          cuisine: "Local Specialties",
          tier: "Signature",
        },
      };
    }

    if (lower.includes("restart") || lower.includes("start over")) {
      return {
        type: "command",
        command: "restart",
      };
    }

    if (lower.includes("change") || lower.includes("modify")) {
      return {
        type: "command",
        command: "modify_choices",
      };
    }

    return null;
  }

  // Get success message for a choice
  getSuccessMessage(stage, choice) {
    const config = this.stageOptions[stage];
    if (!config) return `You selected ${choice}`;

    return config.successMessage.replace("{choice}", choice);
  }

  // Get next stage in flow
  getNextStage(currentStage) {
    const currentIndex = this.stageFlow.indexOf(currentStage);
    if (currentIndex === -1 || currentIndex === this.stageFlow.length - 1) {
      return this.stages.COMPLETE;
    }

    return this.stageFlow[currentIndex + 1];
  }

  // Build AI prompt with context
  buildAIPrompt(message, context) {
    const { currentStage, userChoices } = context;

    let contextInfo = "Current conversation context:\n";

    if (userChoices.activity) {
      contextInfo += `- User selected activity: ${userChoices.activity}\n`;
    }
    if (userChoices.cuisine) {
      contextInfo += `- User selected cuisine: ${userChoices.cuisine}\n`;
    }
    if (userChoices.tier) {
      contextInfo += `- User selected tier: ${userChoices.tier}\n`;
    }

    let stageGuidance = "";
    if (this.stageOptions[currentStage]) {
      stageGuidance = `\nCurrent stage: ${this.stageOptions[currentStage].aiPrompt}`;
    }

    return `${contextInfo}\nUser says: "${message}"${stageGuidance}\n\nPlease respond naturally and guide to next step if appropriate.`;
  }
}